import { Dispatch, SetStateAction, ChangeEvent, WheelEvent } from "react";
import PopupManager from "@/components/PopupManager";
import { setGlobalPopupElement } from "@/store/features/globalSlice";
import { useAppDispatch } from "@/store/hooks";
import { emiTenors } from "@/utils/constants";
import ManageGiftQty from "../ManageGiftQty";

const offerItems: OfferLogicItem[] = [
  { type: "EMI", label: "EMI" },
  { type: "GNG", label: "G&G Gifts" },
  { type: "BRAND", label: "Brand Gifts" },
  { type: "DISCOUNT", label: "Discount" },
];

const emiTypeOptions = [
  { label: "Online EMI", value: "online" },
  { label: "On Demand EMI", value: "on-demand" },
];

interface Props {
  offersState: OfferLogicState;
  setOffersState: Dispatch<SetStateAction<OfferLogicState>>;
}

const OfferLogicsForm = ({ offersState, setOffersState }: Props) => {
  const dispatch = useAppDispatch();

  const selectHandler = (type: OfferLogicType, value: boolean) => {
    if ((type === "GNG" || type === "BRAND") && value) {
      const giftData = offersState[type] as OfferLogicData;
      dispatch(
        setGlobalPopupElement(
          <PopupManager>
            <ManageGiftQty
              initialGifts={(giftData.gifts || []).map((gift) => ({
                id: gift.id,
                name: gift.name,
                media: gift.image,
                slug: "",
                qty: String(gift.quantity || 1),
                status: "active",
                mediaId: 0,
                price: 0,
                quantity: gift.quantity,
                amount: gift.amount,
              }))}
              initialMaxGifts={giftData.maxGifts || 1}
              onSubmit={(gifts, maxGifts) => {
                setOffersState((prev) => ({
                  ...prev,
                  [type]: {
                    ...prev[type],
                    gifts: gifts.map((gift) => ({
                      id: gift.id,
                      name: gift.name,
                      image: gift.media || "",
                      quantity: gift.quantity || 1,
                      amount: gift.amount,
                    })),
                    maxGifts: maxGifts,
                  },
                }));

                dispatch(setGlobalPopupElement(null));
              }}
            />
          </PopupManager>,
        ),
      );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {offerItems.map((offer, index) => {
        const active = offersState[offer.type]?.selected;

        return (
          <div
            key={offer.type}
            className={`
              ${index === 0 ? "" : "border-t border-gray-200"}
              ${index === offerItems.length - 1 ? "" : "border-b border-gray-200"}
              px-4 py-2
            `}
          >
            <div className="flex items-center gap-4">
              {/* Mandatory flag */}
              <button
                type="button"
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                title="Mandatory"
                onClick={() => {
                  if (!offersState[offer.type]?.selected)
                    selectHandler(offer.type, !offersState[offer.type]?.mandatory);

                  setOffersState((prev) => ({
                    ...prev,
                    [offer.type]: {
                      ...prev[offer.type],
                      mandatory: !prev[offer.type]?.mandatory,
                      selected: !prev[offer.type]?.mandatory
                        ? true
                        : prev[offer.type]?.selected,
                    },
                  }));
                }}
              >
                {offersState[offer.type]?.mandatory ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                    <path d="M12.36 6l.4 2H18v6h-3.36l-.4-2H7V6h5.36M14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6L14 4z"/>
                  </svg>
                )}
              </button>

              {/* Active checkbox */}
              <button
                type="button"
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                title="Active"
                onClick={() => {
                  selectHandler(offer.type, !offersState[offer.type]?.selected);

                  setOffersState((prev) => ({
                    ...prev,
                    [offer.type]: {
                      ...prev[offer.type],
                      selected: !prev[offer.type]?.selected,
                    },
                  }));
                }}
              >
                {offersState[offer.type]?.selected ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-5.8l-2.59-2.59L6 13l4 4 8-8-1.41-1.42z"/>
                  </svg>
                )}
              </button>

              <span className="text-sm font-medium text-gray-700">{offer.label}</span>

              {/* GNG / BRAND - edit gifts button */}
              {(offer.type === "GNG" || offer.type === "BRAND") && active && (
                <button
                  type="button"
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                  onClick={() => selectHandler(offer.type, true)}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-600" fill="currentColor">
                    <path d="M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4-3.58-4-8-4-8 1.79-8 4zm0 5c0 2.21 3.58 4 8 4s8-1.79 8-4m0-5v4c0 2.21-3.58 4-8 4s-8-1.79-8-4V7m0 10c0 2.21 3.58 4 8 4s8-1.79 8-4m-4.9-6.7l.7-.7 3.2 3.2-.7.7-3.2-3.2zm-4.5 4.5l.7-.7 3.2 3.2-.7.7-3.2-3.2z"/>
                  </svg>
                </button>
              )}

              {/* EMI - type selection */}
              {offer.type === "EMI" && active && (
                <div className="flex items-center gap-2">
                  {emiTypeOptions.map((emiType) => {
                    const emiActive =
                      offersState[offer.type]?.emiTypes?.includes(
                        emiType.value as "online" | "on-demand",
                      ) || false;

                    return (
                      <div key={emiType.value} className="flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                          title="Active"
                          onClick={() => {
                            setOffersState((prev) => ({
                              ...prev,
                              [offer.type]: {
                                ...prev[offer.type],
                                emiTypes: !emiActive
                                  ? ([emiType.value] as ("online" | "on-demand")[])
                                  : [],
                              },
                            }));
                          }}
                        >
                          {emiActive ? (
                            <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-5.8l-2.59-2.59L6 13l4 4 8-8-1.41-1.42z"/>
                            </svg>
                          )}
                        </button>

                        <span className="text-sm text-gray-700">{emiType.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* EMI - tenor selection */}
            {offer.type === "EMI" && active && (
              <div className="flex items-center flex-wrap gap-4 mt-2">
                <div className="flex gap-2">
                  EMI Tenors
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </div>
                {emiTenors.map((tenor: EMITenor) => {
                  const tenorActive = !!offersState[offer.type]?.emiTenors?.find(
                    (t) => t.name === tenor.name,
                  );

                  return (
                    <div key={tenor.name} className="flex items-center gap-1">
                      <button
                        type="button"
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                        title="Active"
                        onClick={() => {
                          setOffersState((prev) => {
                            const currentTenors =
                              prev[offer.type]?.emiTenors || [];
                            const updatedTenors = !tenorActive
                              ? [...currentTenors, tenor]
                              : currentTenors.filter(
                                  (t) => t.name !== tenor.name,
                                );

                            return {
                              ...prev,
                              [offer.type]: {
                                ...prev[offer.type],
                                emiTenors: updatedTenors.sort(
                                  (a, b) =>
                                    +a.name.split(" ")[0] -
                                    +b.name.split(" ")[0],
                                ),
                              },
                            };
                          });
                        }}
                      >
                        {tenorActive ? (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-5.8l-2.59-2.59L6 13l4 4 8-8-1.41-1.42z"/>
                          </svg>
                        )}
                      </button>

                      <span className="text-sm text-gray-700">{tenor.name}</span>

                      {tenorActive &&
                        offersState.EMI?.emiTypes?.[0] !== "online" && (
                          <input
                            type="number"
                            placeholder="(%)"
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={
                              offersState[offer.type]?.emiTenors?.find(
                                (t) => t.name === tenor.name,
                              )?.percent || 0
                            }
                            onWheel={(e: WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const val = +e.target.value;
                              setOffersState((prev) => ({
                                ...prev,
                                [offer.type]: {
                                  ...prev[offer.type],
                                  emiTenors:
                                    prev[offer.type]?.emiTenors?.map((t) => {
                                      if (t.name === tenor.name)
                                        return { ...t, percent: val };
                                      return t;
                                    }),
                                },
                              }));
                            }}
                          />
                        )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Forbidden items */}
            {active && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex gap-2">
                  Forbidden Items
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </div>
                {offerItems.map((item) => {
                  if (item.type === offer.type) return null;

                  const isForbidden =
                    !!offersState[offer.type]?.forbiddens?.includes(item.type);
                  const isMandatory =
                    !!offersState[offer.type]?.mandatories?.includes(item.type);

                  return (
                    <div key={item.type} className="flex items-center gap-1">
                      <button
                        type="button"
                        className={`p-1 rounded-full hover:bg-gray-100 transition-colors ${isMandatory ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Active"
                        disabled={isMandatory}
                        onClick={() => {
                          setOffersState((prev) => ({
                            ...prev,
                            [offer.type]: {
                              ...prev[offer.type],
                              forbiddens: !isForbidden
                                ? [
                                    ...(prev[offer.type]?.forbiddens || []),
                                    item.type,
                                  ]
                                : (prev[offer.type]?.forbiddens || []).filter(
                                    (type) => type !== item.type,
                                  ),
                            },
                          }));
                        }}
                      >
                        {isForbidden ? (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-5.8l-2.59-2.59L6 13l4 4 8-8-1.41-1.42z"/>
                          </svg>
                        )}
                      </button>

                      <span className="text-sm text-gray-700">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Mandatory items */}
            {active && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex gap-2">
                  Mandatory Items
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </div>
                {offerItems.map((item) => {
                  if (item.type === offer.type) return null;

                  const isMandatory =
                    !!offersState[offer.type]?.mandatories?.includes(item.type);
                  const isForbidden =
                    !!offersState[offer.type]?.forbiddens?.includes(item.type);

                  return (
                    <div key={item.type} className="flex items-center gap-1">
                      <button
                        type="button"
                        className={`p-1 rounded-full hover:bg-gray-100 transition-colors ${isForbidden ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Active"
                        disabled={isForbidden}
                        onClick={() => {
                          setOffersState((prev) => ({
                            ...prev,
                            [offer.type]: {
                              ...prev[offer.type],
                              mandatories: !isMandatory
                                ? [
                                    ...(prev[offer.type]?.mandatories || []),
                                    item.type,
                                  ]
                                : (prev[offer.type]?.mandatories || []).filter(
                                    (type) => type !== item.type,
                                  ),
                            },
                          }));
                        }}
                      >
                        {isMandatory ? (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-5.8l-2.59-2.59L6 13l4 4 8-8-1.41-1.42z"/>
                          </svg>
                        )}
                      </button>

                      <span className="text-sm text-gray-700">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OfferLogicsForm;
